import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Brain, LogOut, Eye, FileText, DollarSign, Calendar, Send, MessageCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [intakeForm, setIntakeForm] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundBooking, setRefundBooking] = useState(null);
  const [appointmentDatetime, setAppointmentDatetime] = useState('');
  const [refundPercentage, setRefundPercentage] = useState(100);
  const [hoursUntilAppointment, setHoursUntilAppointment] = useState(0);
  const [processingRefund, setProcessingRefund] = useState(false);
  
  // Manual intake form sending
  const [showIntakeFormModal, setShowIntakeFormModal] = useState(false);
  const [intakeFormData, setIntakeFormData] = useState({
    clientName: '',
    clientEmail: '',
    bookingId: ''
  });
  const [sendingIntakeForm, setSendingIntakeForm] = useState(false);
  const [bookingSearch, setBookingSearch] = useState('');
  const [showBookingDropdown, setShowBookingDropdown] = useState(false);
  const [resendingBookingId, setResendingBookingId] = useState(null);

  // Define fetchBookings first (before handleLogin which depends on it)
  const fetchBookings = useCallback(async (pass = password) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/bookings?password=${pass}`);
      if (response.data.error) {
        toast.error('Unauthorized');
        handleLogout();
      } else {
        setBookings(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch bookings');
    }
    setLoading(false);
  }, [password]);

  const handleLogin = useCallback(async (e, savedPass = null) => {
    if (e) e.preventDefault();
    const passToUse = savedPass || password;
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password: passToUse });
      if (response.data.status === 'success') {
        setIsAuthenticated(true);
        // Note: sessionStorage used for convenience. For production, consider httpOnly cookies
        sessionStorage.setItem('admin_password', passToUse);
        toast.success('Login successful');
        fetchBookings(passToUse);
      } else {
        toast.error('Invalid password');
      }
    } catch (error) {
      toast.error('Login failed');
    }
  }, [password, fetchBookings]);

  useEffect(() => {
    document.title = 'Admin Dashboard - MIND FRAME';
    const savedPassword = sessionStorage.getItem('admin_password');
    if (savedPassword) {
      setPassword(savedPassword);
      handleLogin(null, savedPassword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewIntakeForm = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/admin/intake-form/${bookingId}?password=${password}`);
      if (response.data.error) {
        toast.error('Intake form not submitted yet');
      } else {
        setIntakeForm(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch intake form');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    sessionStorage.removeItem('admin_password');
    setBookings([]);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getPaymentStatusClass = (status) => {
    if (status === 'paid') return 'bg-green-100 text-green-700';
    if (status === 'pay_later') return 'bg-orange-100 text-orange-700';
    if (status && status.startsWith('refunded')) return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getPaymentStatusText = (status) => {
    if (status === 'paid') return 'Paid';
    if (status === 'pay_later') return 'Pay Later';
    if (status && status.startsWith('refunded')) return status.replace('refunded_', 'Refunded ');
    return 'Pending';
  };

  const openRefundModal = (booking) => {
    setRefundBooking(booking);
    setShowRefundModal(true);
    setAppointmentDatetime('');
    setRefundPercentage(100);
    setHoursUntilAppointment(0);
  };

  const calculateRefundPercentage = (datetimeString) => {
    if (!datetimeString) {
      setRefundPercentage(100);
      setHoursUntilAppointment(0);
      return;
    }

    try {
      const appointmentTime = new Date(datetimeString);
      const currentTime = new Date();
      const hoursUntil = (appointmentTime - currentTime) / (1000 * 60 * 60);
      
      setHoursUntilAppointment(hoursUntil);
      
      if (hoursUntil >= 24) {
        setRefundPercentage(100);
      } else {
        setRefundPercentage(50);
      }
    } catch (error) {
      setRefundPercentage(100);
      setHoursUntilAppointment(0);
    }
  };

  const handleDatetimeChange = (e) => {
    const value = e.target.value;
    setAppointmentDatetime(value);
    calculateRefundPercentage(value);
  };

  const processRefund = async () => {
    if (!appointmentDatetime) {
      toast.error('Please enter the appointment date and time');
      return;
    }

    setProcessingRefund(true);
    try {
      const response = await axios.post(`${API}/admin/refund`, null, {
        params: {
          booking_id: refundBooking.id,
          appointment_datetime: new Date(appointmentDatetime).toISOString(),
          password: password
        }
      });

      if (response.data.error) {
        toast.error(response.data.error);
      } else {
        toast.success(response.data.message);
        setShowRefundModal(false);
        fetchBookings();
      }
    } catch (error) {
      toast.error('Failed to process refund');
    }
    setProcessingRefund(false);
  };

  const handleSendIntakeForm = async () => {
    if (!intakeFormData.clientName || !intakeFormData.clientEmail) {
      toast.error('Please fill in all fields');
      return;
    }

    setSendingIntakeForm(true);
    try {
      const response = await axios.post(`${API}/admin/send-intake-form`, {
        client_name: intakeFormData.clientName,
        client_email: intakeFormData.clientEmail,
        booking_id: intakeFormData.bookingId || null,
        password: password,
        frontend_url: window.location.origin
      });

      if (response.data.status === 'success') {
        toast.success('Intake form sent successfully!');
        setShowIntakeFormModal(false);
        setIntakeFormData({ clientName: '', clientEmail: '', bookingId: '' });
        setBookingSearch('');
        setShowBookingDropdown(false);
      } else {
        toast.error(response.data.message || 'Failed to send intake form');
      }
    } catch (error) {
      toast.error('Failed to send intake form');
    }
    setSendingIntakeForm(false);
  };

  const handleResendIntakeForm = async (booking) => {
    setResendingBookingId(booking.id);
    try {
      const response = await axios.post(`${API}/admin/send-intake-form`, {
        client_name: booking.name,
        client_email: booking.email,
        booking_id: booking.id,
        password: password,
        frontend_url: window.location.origin
      });
      if (response.data.status === 'success') {
        toast.success(`Intake form resent to ${booking.email}`);
      } else {
        toast.error(response.data.message || 'Failed to resend intake form');
      }
    } catch (error) {
      toast.error('Failed to resend intake form');
    }
    setResendingBookingId(null);
  };

  const handleWhatsAppShareIntake = (booking) => {
    // Normalize phone: strip non-digits and add country code if missing
    let phone = (booking.phone || '').replace(/\D/g, '');
    if (phone.length === 10) phone = `91${phone}`;
    const intakeUrl = `${window.location.origin}/intake`;
    const message = `Hi ${booking.name || ''}, thank you for booking your session with MIND FRAME. Please complete this intake form before your session (it takes about 5 minutes):\n\n${intakeUrl}\n\nYou can either fill it online and save as PDF, or send it back via WhatsApp directly from the form. Looking forward to speaking with you.\n\nGaurav Juneja\nMIND FRAME Counselling`;
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleCopyIntakeLink = async (booking) => {
    const intakeUrl = `${window.location.origin}/intake`;
    try {
      await navigator.clipboard.writeText(intakeUrl);
      toast.success('Intake form link copied to clipboard');
    } catch {
      toast.error('Could not copy — link: ' + intakeUrl);
    }
  };



  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800">MIND FRAME</h1>
            </div>
            <CardTitle>Admin Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input 
                  id="password" 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                  placeholder="Enter admin password"
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-teal-600 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Admin Dashboard</h1>
              <p className="text-sm text-slate-600">MIND FRAME Counselling</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/MIND-FRAME-Intake-Form.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="download-intake-pdf"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium bg-blue-50 border border-blue-600 text-blue-700 hover:bg-blue-100 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Intake PDF
            </a>
            <Button 
              onClick={() => setShowIntakeFormModal(true)}
              variant="outline"
              size="sm"
              className="gap-2 bg-teal-50 border-teal-600 text-teal-700 hover:bg-teal-100"
            >
              <FileText className="w-4 h-4" />
              Send Intake Form
            </Button>
            <Button onClick={fetchBookings} variant="outline" size="sm">
              Refresh
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm" className="gap-2">
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Bookings</p>
                  <p className="text-3xl font-bold text-blue-600">{bookings.length}</p>
                </div>
                <Calendar className="w-10 h-10 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Paid</p>
                  <p className="text-3xl font-bold text-green-600">
                    {bookings.filter(b => b.paymentStatus === 'paid').length}
                  </p>
                </div>
                <DollarSign className="w-10 h-10 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Pay Later</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {bookings.filter(b => b.paymentStatus === 'pay_later' || b.paymentMode === 'pay_later').length}
                  </p>
                </div>
                <DollarSign className="w-10 h-10 text-orange-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Intake Forms</p>
                  <p className="text-3xl font-bold text-teal-600">
                    {bookings.filter(b => b.has_intake_form).length}
                  </p>
                </div>
                <FileText className="w-10 h-10 text-teal-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-slate-600">Loading...</p>
            ) : bookings.length === 0 ? (
              <p className="text-center py-8 text-slate-600">No bookings yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-slate-700">Date</th>
                      <th className="text-left p-3 text-sm font-semibold text-slate-700">Name</th>
                      <th className="text-left p-3 text-sm font-semibold text-slate-700">Email</th>
                      <th className="text-left p-3 text-sm font-semibold text-slate-700">Phone</th>
                      <th className="text-left p-3 text-sm font-semibold text-slate-700">Payment</th>
                      <th className="text-left p-3 text-sm font-semibold text-slate-700">Intake Form</th>
                      <th className="text-left p-3 text-sm font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking, idx) => (
                      <tr key={booking.id} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                        <td className="p-3 text-sm text-slate-600">{formatDate(booking.timestamp)}</td>
                        <td className="p-3 text-sm font-medium text-slate-800">{booking.name}</td>
                        <td className="p-3 text-sm text-slate-600">{booking.email}</td>
                        <td className="p-3 text-sm text-slate-600">{booking.phone}</td>
                        <td className="p-3">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getPaymentStatusClass(booking.paymentStatus)}`}>
                            {getPaymentStatusText(booking.paymentStatus)}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            booking.has_intake_form 
                              ? 'bg-teal-100 text-teal-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {booking.has_intake_form ? 'Submitted' : 'Pending'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => setSelectedBooking(booking)}
                              className="gap-1"
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </Button>
                            {booking.has_intake_form && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => viewIntakeForm(booking.id)}
                                className="gap-1"
                              >
                                <FileText className="w-3 h-3" />
                                Intake
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleWhatsAppShareIntake(booking)}
                              data-testid={`whatsapp-intake-${booking.id}`}
                              className="gap-1 text-green-700 hover:text-green-800 hover:bg-green-50"
                            >
                              <MessageCircle className="w-3 h-3" />
                              WhatsApp
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCopyIntakeLink(booking)}
                              data-testid={`copy-intake-${booking.id}`}
                              className="gap-1"
                            >
                              <Copy className="w-3 h-3" />
                              Copy Link
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResendIntakeForm(booking)}
                              disabled={resendingBookingId === booking.id}
                              data-testid={`resend-intake-${booking.id}`}
                              className="gap-1 text-teal-700 hover:text-teal-800 hover:bg-teal-50"
                            >
                              <Send className="w-3 h-3" />
                              {resendingBookingId === booking.id ? 'Sending...' : 'Resend'}
                            </Button>
                            {booking.paymentStatus === 'paid' && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => openRefundModal(booking)}
                                className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <DollarSign className="w-3 h-3" />
                                Refund
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Booking Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Name</p>
                  <p className="font-semibold">{selectedBooking.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Age</p>
                  <p className="font-semibold">{selectedBooking.age}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Email</p>
                  <p className="font-semibold">{selectedBooking.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Phone</p>
                  <p className="font-semibold">{selectedBooking.phone}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-600">Concern</p>
                <p className="mt-1 p-3 bg-slate-50 rounded">{selectedBooking.concern}</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Emergency Contact</p>
                  <p className="font-semibold">{selectedBooking.emergencyContact}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Emergency Phone</p>
                  <p className="font-semibold">{selectedBooking.emergencyPhone}</p>
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <Button onClick={() => setSelectedBooking(null)} className="flex-1">Close</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Intake Form Modal */}
      {intakeForm && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Client Intake Form</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold text-lg mb-3">Medical History</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Current Medications:</strong> {intakeForm.current_medications || 'None'}</div>
                  <div><strong>Medical Conditions:</strong> {intakeForm.medical_conditions || 'None'}</div>
                  <div><strong>Allergies:</strong> {intakeForm.allergies || 'None'}</div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-3">Mental Health History</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Previous Therapy:</strong> {intakeForm.previous_therapy || 'None'}</div>
                  <div><strong>Previous Diagnosis:</strong> {intakeForm.previous_diagnosis || 'None'}</div>
                  <div><strong>Family History:</strong> {intakeForm.family_mental_health || 'None'}</div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-3">Current Situation</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Symptoms:</strong> {intakeForm.current_symptoms}</div>
                  <div><strong>Duration:</strong> {intakeForm.symptom_duration || 'Not specified'}</div>
                  <div><strong>Triggers:</strong> {intakeForm.triggers || 'None mentioned'}</div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-3">Therapy Goals</h3>
                <p className="text-sm">{intakeForm.therapy_goals}</p>
              </div>
              <div className="flex gap-4 pt-4">
                <Button onClick={() => setIntakeForm(null)} className="flex-1">Close</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && refundBooking && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>Process Refund</CardTitle>
              <CardDescription>Enter appointment details to calculate refund automatically</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Client:</span>
                  <span className="font-semibold">{refundBooking.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Email:</span>
                  <span className="font-semibold">{refundBooking.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Payment ID:</span>
                  <span className="font-mono text-xs">{refundBooking.paymentId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Amount Paid:</span>
                  <span className="font-semibold text-green-600">₹800</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="appointmentDatetime">Appointment Date & Time</Label>
                <Input 
                  id="appointmentDatetime"
                  type="datetime-local"
                  value={appointmentDatetime}
                  onChange={handleDatetimeChange}
                  required
                  className="text-base"
                />
                <p className="text-xs text-slate-500">Enter the scheduled appointment time from Calendly</p>
              </div>

              {appointmentDatetime && (
                <div className={`p-4 rounded-lg border-2 ${refundPercentage === 100 ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Time Until Appointment:</span>
                      <span className="font-bold text-lg">{hoursUntilAppointment.toFixed(1)} hours</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Refund Percentage:</span>
                      <span className={`font-bold text-2xl ${refundPercentage === 100 ? 'text-green-600' : 'text-orange-600'}`}>
                        {refundPercentage}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Refund Amount:</span>
                      <span className={`font-bold text-xl ${refundPercentage === 100 ? 'text-green-600' : 'text-orange-600'}`}>
                        ₹{refundPercentage === 100 ? '800' : '400'}
                      </span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-slate-300">
                      <p className="text-xs text-slate-600">
                        {refundPercentage === 100 
                          ? '✓ Cancellation is 24+ hours before appointment - Full refund (100%)'
                          : '⚠ Cancellation is less than 24 hours before appointment - Partial refund (50%)'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <Button 
                  onClick={() => setShowRefundModal(false)} 
                  variant="outline" 
                  className="flex-1"
                  disabled={processingRefund}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={processRefund} 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={processingRefund || !appointmentDatetime}
                >
                  {processingRefund ? 'Processing...' : `Process ${refundPercentage}% Refund`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Send Intake Form Modal */}
      {showIntakeFormModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Send Intake Form Manually</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 relative">
                <Label htmlFor="bookingSearch">Search Existing Booking (Optional)</Label>
                <Input
                  id="bookingSearch"
                  data-testid="intake-booking-search-input"
                  placeholder="Search by name or email..."
                  value={bookingSearch}
                  onChange={(e) => {
                    setBookingSearch(e.target.value);
                    setShowBookingDropdown(true);
                  }}
                  onFocus={() => setShowBookingDropdown(true)}
                />
                {showBookingDropdown && bookingSearch.trim() && (
                  <div
                    data-testid="intake-booking-dropdown"
                    className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg"
                  >
                    {bookings
                      .filter((b) => {
                        const q = bookingSearch.toLowerCase();
                        return (
                          (b.name || '').toLowerCase().includes(q) ||
                          (b.email || '').toLowerCase().includes(q)
                        );
                      })
                      .slice(0, 8)
                      .map((b) => (
                        <button
                          key={b.id}
                          type="button"
                          data-testid={`intake-booking-option-${b.id}`}
                          onClick={() => {
                            setIntakeFormData({
                              clientName: b.name || '',
                              clientEmail: b.email || '',
                              bookingId: b.id || ''
                            });
                            setBookingSearch(`${b.name} (${b.email})`);
                            setShowBookingDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-slate-100 border-b border-slate-100 last:border-b-0"
                        >
                          <div className="text-sm font-medium text-slate-800">{b.name}</div>
                          <div className="text-xs text-slate-500">{b.email}</div>
                        </button>
                      ))}
                    {bookings.filter((b) => {
                      const q = bookingSearch.toLowerCase();
                      return (
                        (b.name || '').toLowerCase().includes(q) ||
                        (b.email || '').toLowerCase().includes(q)
                      );
                    }).length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-500">No matching bookings</div>
                    )}
                  </div>
                )}
                <p className="text-xs text-slate-500">Pick an existing booking to auto-fill, or enter details below manually.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientName">Client Name *</Label>
                <Input
                  id="clientName"
                  placeholder="Enter client's full name"
                  value={intakeFormData.clientName}
                  onChange={(e) => setIntakeFormData({...intakeFormData, clientName: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientEmail">Client Email *</Label>
                <Input
                  id="clientEmail"
                  type="email"
                  placeholder="client@example.com"
                  value={intakeFormData.clientEmail}
                  onChange={(e) => setIntakeFormData({...intakeFormData, clientEmail: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bookingId">Booking ID (Optional)</Label>
                <Input
                  id="bookingId"
                  placeholder="Leave empty to auto-generate"
                  value={intakeFormData.bookingId}
                  onChange={(e) => setIntakeFormData({...intakeFormData, bookingId: e.target.value})}
                />
                <p className="text-xs text-slate-500">If left empty, a unique ID will be generated automatically</p>
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  onClick={() => {
                    setShowIntakeFormModal(false);
                    setIntakeFormData({ clientName: '', clientEmail: '', bookingId: '' });
                    setBookingSearch('');
                    setShowBookingDropdown(false);
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={sendingIntakeForm}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendIntakeForm}
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  disabled={sendingIntakeForm}
                >
                  {sendingIntakeForm ? 'Sending...' : 'Send Intake Form'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
